import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { GettingStartedFacade } from '@core/getting-started-legacy';
import { GettingStartedSubtask } from '@core/getting-started-types';
import { I18nService } from '@core/i18n-legacy';
import {
  flexiblePositionStrategyFactory,
  globalPositionStrategyFactory,
  type Step,
  WalkthroughDialogComponent,
  WalkthroughNavigationHelperService,
  WalkthroughPostComponent,
  WalkthroughStepsService,
  WalkthroughTooltipComponent,
} from '@core/walkthrough-legacy';
import { walkthroughAssetsResolver } from '@core/walkthrough-util';

@Injectable({ providedIn: 'root' })
export class ProceedDisqualifyWalkthroughService {
  private readonly destroyRef = inject(DestroyRef);

  public readonly subtask = GettingStartedSubtask.ProceedOrDisqualify;

  private steps: Step[] = [
    {
      id: 'walkthrough-step-proceed-disqualify-1',
      position: 1,
      component: {
        definition: WalkthroughDialogComponent,
        inputs: {
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.proceed_disqualify.step_1.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.proceed_disqualify.step_1.description',
          ),
          imageUrl: walkthroughAssetsResolver('proceed_disqualify.svg'),
          ctaText: this.i18n.translate(
            'app.walkthrough.walkthroughs.proceed_disqualify.step_1.cta_text',
          ),
        },
      },
      renderOptions: {
        strategy: globalPositionStrategyFactory,
      },
    },
    {
      id: 'walkthrough-step-proceed-disqualify-2',
      position: 2,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'right'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.proceed_disqualify.step_2.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.proceed_disqualify.step_2.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' }],
        offset: { offsetX: -24, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-proceed-disqualify-3',
      position: 3,

      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['bottom', 'right'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.proceed_disqualify.step_3.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.proceed_disqualify.step_3.description_v2',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'top' }],
        offset: { offsetX: -25, offsetY: -113 },
      },
    },
    {
      id: 'walkthrough-step-proceed-disqualify-4',
      position: 4,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['right', 'bottom'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.proceed_disqualify.step_4.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.proceed_disqualify.step_4.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'bottom' }],
        offset: {
          offsetX: 55,
          offsetY: -54,
        },
      },
    },
    {
      id: 'walkthrough-step-proceed-disqualify-5',
      position: 5,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'right'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.proceed_disqualify.step_5.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.proceed_disqualify.step_5.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' }],
        offset: { offsetX: -24, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-proceed-disqualify-6',
      position: 6,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'top'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.proceed_disqualify.step_6.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.proceed_disqualify.step_6.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top' }],
        offset: { offsetX: 0, offsetY: -36 },
      },
    },
    {
      id: 'walkthrough-step-proceed-disqualify-7',
      position: 7,

      component: {
        definition: WalkthroughPostComponent,
        inputs: {
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.proceed_disqualify.step_7.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.proceed_disqualify.step_7.description',
          ),
          ctaText: this.i18n.translate(
            'app.walkthrough.walkthroughs.proceed_disqualify.step_7.cta_text',
          ),
          helpArticleKey: 'disqualifyReasons',
        },
      },
      renderOptions: {
        strategy: globalPositionStrategyFactory,
      },
      finishHandler: () => {
        this.finishHandler();
      },
    },
  ];

  constructor(
    private walkthroughStepsService: WalkthroughStepsService,
    private router: Router,
    private i18n: I18nService,
    private gettingStartedFacade: GettingStartedFacade,
    private navigationHelperService: WalkthroughNavigationHelperService,
  ) {}

  public start(): void {
    this.walkthroughStepsService
      .start(this.steps)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        this.navigationHelperService.handleWalkthroughState(state, this.subtask, ['candidates']);
      });

    this.walkthroughStepsService.onLastStep$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.gettingStartedFacade.markSubtaskAsDone(this.subtask);
      });
  }

  private finishHandler(): void {
    this.router.navigate(['candidates']);
  }
}
